/**
 * Tenant archetype derivation.
 *
 * PureID tenants carry five immutable classification dimensions — tenant
 * type, user source, platform role, identity source, external provider —
 * set at creation and enforced by DB CHECK constraints and by
 * `validateCrossFields` in Go. The full matrix has dozens of theoretical
 * combinations but only five legal outcomes (four create-time + one
 * infrastructure singleton). We call those outcomes "archetypes".
 *
 * The archetype id is stable across releases (persisted in URLs,
 * telemetry, docs) so treat it like a public enum.
 */

import type { LucideIcon } from "lucide-react";
import {
  Building2,
  KeyRound,
  ShieldCheck,
  Users,
  Workflow,
} from "lucide-react";
import type {
  ExternalProvider,
  PlatformRole,
  TenantType,
  UserSource,
} from "./schemas";

export type ArchetypeId =
  | "infrastructure"
  | "workforce_full_idp"
  | "workforce_passkey_layer"
  | "customer_full_idp"
  | "customer_enterprise_fed";

export interface ArchetypeDescriptor {
  id: ArchetypeId;
  /** Short label used on badges and column cells. */
  label: string;
  /** Longer heading for the detail-page architecture card. */
  heading: string;
  /** Single-sentence explanation for tooltips + create confirmation. */
  blurb: string;
  /** Icon surfaced next to the label on cards and chips. */
  icon: LucideIcon;
  /** Theme tone used for the badge + card accent. */
  tone: "primary" | "success" | "warning" | "info" | "secondary";
}

const INFRASTRUCTURE: ArchetypeDescriptor = {
  id: "infrastructure",
  label: "Infrastructure",
  heading: "Platform infrastructure tenant",
  blurb:
    "Singleton tenant that anchors PureID platform admins. Cannot be deleted; not available as a create-time option — the schema enforces exactly one.",
  icon: ShieldCheck,
  tone: "warning",
};

// Archetype labels MUST match the creation-form vocabulary
// (`features/tenants/constants.ts` USER_SOURCES). The operator
// who created the tenant picked from those exact phrases — using
// different language on the settings page makes them wonder if
// they're looking at the same tenant.
const WORKFORCE_FULL_IDP: ArchetypeDescriptor = {
  id: "workforce_full_idp",
  label: "Workforce — PureID directory",
  heading: "Workforce initial setup",
  blurb:
    "Created with PureID user management. Current sign-in policies and provisioning connections determine how users enter and authenticate.",
  icon: Users,
  tone: "primary",
};

const WORKFORCE_PASSKEY_LAYER: ArchetypeDescriptor = {
  id: "workforce_passkey_layer",
  label: "Workforce — External directory with PureID passkeys",
  heading: "Workforce initial setup",
  blurb:
    "Set up for an external directory to manage users and PureID to authenticate their passkeys. Provisioning connections and sign-in policies must be configured separately.",
  icon: KeyRound,
  tone: "info",
};

const CUSTOMER_FULL_IDP: ArchetypeDescriptor = {
  id: "customer_full_idp",
  label: "Customer — PureID authentication",
  heading: "Customer initial setup",
  blurb:
    "Created with PureID customer identity. Each organization's current sign-in policy and provisioning connections determine admission and authentication, including externally managed users.",
  icon: Building2,
  tone: "success",
};

const CUSTOMER_ENTERPRISE_FED: ArchetypeDescriptor = {
  id: "customer_enterprise_fed",
  label: "Customer — External identity provider sign-in",
  heading: "Customer initial setup",
  blurb:
    "Created for customer organizations with external identity providers. Each organization's active connections and sign-in policy determine whether federated authentication is available.",
  icon: Workflow,
  tone: "secondary",
};

/** Ordered list used by the create form's archetype cards.
 *  Excludes `infrastructure` — the singleton is not user-choosable. */
export const CREATE_ARCHETYPES: readonly ArchetypeDescriptor[] = [
  WORKFORCE_FULL_IDP,
  WORKFORCE_PASSKEY_LAYER,
  CUSTOMER_FULL_IDP,
  CUSTOMER_ENTERPRISE_FED,
];

/** Every archetype. Used by read paths for filter chips and
 *  reverse-lookup. */
export const ALL_ARCHETYPES: readonly ArchetypeDescriptor[] = [
  INFRASTRUCTURE,
  WORKFORCE_FULL_IDP,
  WORKFORCE_PASSKEY_LAYER,
  CUSTOMER_FULL_IDP,
  CUSTOMER_ENTERPRISE_FED,
];

/**
 * Resolve a tenant's archetype from its raw classification fields.
 *
 * `is_infrastructure=true` wins over the other dimensions — the
 * platform-admin anchor tenant is the same archetype regardless of
 * what the other dimensions happen to be.
 *
 * Every other (tenant_type, user_source, platform_role) combination
 * that reaches this function has already been validated by the
 * backend's `validateCrossFields`, so the switch is exhaustive over
 * the legal set. A combination that slips through (hand-crafted DB
 * row, corrupt state) will hit the final `throw` — louder than a
 * silent fallback and easier to trace.
 */
export function deriveArchetype(input: {
  is_infrastructure: boolean;
  tenant_type: TenantType;
  user_source: UserSource;
  platform_role: PlatformRole;
}): ArchetypeDescriptor {
  if (input.is_infrastructure) return INFRASTRUCTURE;
  const { tenant_type, user_source, platform_role } = input;
  // platform_role is always full_idp on shipping deployments —
  // see backend tenant.go for the auth_edge removal context.
  if (platform_role !== "full_idp") {
    throw new Error(
      `deriveArchetype: unexpected platform_role=${platform_role}; only full_idp is legal post-auth_edge-removal`,
    );
  }
  if (tenant_type === "workforce") {
    if (user_source === "complete_platform") return WORKFORCE_FULL_IDP;
    if (user_source === "existing_idp") return WORKFORCE_PASSKEY_LAYER;
  }
  if (tenant_type === "customer") {
    if (user_source === "complete_platform") return CUSTOMER_FULL_IDP;
    if (user_source === "enterprise_fed") return CUSTOMER_ENTERPRISE_FED;
  }
  throw new Error(
    `deriveArchetype: illegal dimension combination tenant_type=${tenant_type} user_source=${user_source} platform_role=${platform_role} — backend validateCrossFields should have rejected this`,
  );
}

/**
 * Pretty-print an external provider id for UI display. Capitalisation
 * matches how each vendor writes its own name on the web.
 * `oidc-saml` is our catch-all for generic upstream protocols.
 */
export function externalProviderLabel(p: ExternalProvider | undefined): string {
  switch (p) {
    case "entra":
      return "Microsoft Entra";
    case "okta":
      return "Okta";
    case "ping":
      return "Ping Identity";
    case "google":
      return "Google Workspace";
    case "oidc-saml":
      return "Generic OIDC/SAML";
    default:
      return "—";
  }
}
